import ActionDialog from "components/shared/ActionDialog";
import React, { useEffect, useRef, useState, useCallback } from "react";
import WarningIcon from "assets/icons/warning-yellow.svg?react";
import { BeatLoader } from "react-spinners";
import { useSelector } from "react-redux";
import { RootState } from "redux/store/store";
import { translations, t } from "utils/i18n";
import { useLogout } from "hooks/useLogout";

const IDLE_TIMEOUT = 5 * 60 * 1000; // 9 seconds (for quick testing)
const COUNTDOWN_TIME = 60; // 60 seconds

const IdleTimerComponent: React.FC = () => {
  const [isInactive, setIsInactive] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_TIME);
  const [showLoader, setShowLoader] = useState(false);
  const autoLogoutEnabled = useSelector(
    (state: RootState) => state.autoLogout.enabled,
  );

  const idleTimeoutId = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalId = useRef<NodeJS.Timeout | null>(null);
  const countdownEndAtRef = useRef<number | null>(null);
  const hasCountdownStartedRef = useRef(false); //  prevent restart

  const clearAllTimers = () => {
    if (idleTimeoutId.current) {
      clearTimeout(idleTimeoutId.current);
      idleTimeoutId.current = null;
    }
    if (countdownIntervalId.current) {
      clearInterval(countdownIntervalId.current);
      countdownIntervalId.current = null;
    }
    countdownEndAtRef.current = null;
    hasCountdownStartedRef.current = false;
  };

  const { logout: logoutUser } = useLogout();

  const handleLogout = useCallback(async () => {
    clearAllTimers();
    await logoutUser();
  }, [logoutUser]);

  const startCountdown = useCallback(() => {
    if (hasCountdownStartedRef.current) return; //  Don’t restart if already running
    hasCountdownStartedRef.current = true;

    countdownEndAtRef.current = Date.now() + COUNTDOWN_TIME * 1000;

    const tick = () => {
      if (!countdownEndAtRef.current) return;
      const remainingMs = Math.max(0, countdownEndAtRef.current - Date.now());
      const remainingSec = Math.floor(remainingMs / 1000);
      setCountdown(remainingSec);

      if (remainingMs <= 0) {
        clearInterval(countdownIntervalId.current!);
        countdownIntervalId.current = null;
        handleLogout();
      }
    };

    tick(); // Start immediately
    countdownIntervalId.current = setInterval(tick, 250);
  }, [handleLogout]);

  const startIdleTimer = useCallback(() => {
    if (isInactive) return; //  don't start new timer when dialog is open
    if (!autoLogoutEnabled) return; //  don't start timer if auto logout is disabled
    clearAllTimers();

    idleTimeoutId.current = setTimeout(() => {
      setIsInactive(true);
      setShowLoader(true);
      setCountdown(COUNTDOWN_TIME); // reset countdown
      hasCountdownStartedRef.current = false;

      setTimeout(() => {
        setShowLoader(false);
        startCountdown();
      }, 5000);
    }, IDLE_TIMEOUT);
  }, [isInactive, startCountdown, autoLogoutEnabled]);

  const handleUserActivity = useCallback(() => {
    if (isInactive) return; //  ignore activity when dialog open
    startIdleTimer();
  }, [isInactive, startIdleTimer]);

  useEffect(() => {
    // Only start idle timer if auto logout is enabled
    if (autoLogoutEnabled) {
      startIdleTimer();
      globalThis.addEventListener("mousemove", handleUserActivity);
      globalThis.addEventListener("keydown", handleUserActivity);
    } else {
      // Clear timers if auto logout is disabled
      clearAllTimers();
    }

    return () => {
      globalThis.removeEventListener("mousemove", handleUserActivity);
      globalThis.removeEventListener("keydown", handleUserActivity);
      clearAllTimers();
    };
  }, [handleUserActivity, startIdleTimer, autoLogoutEnabled]);

  const handleConfirmDialog = useCallback(() => {
    //  Resume session
    setIsInactive(false);
    setShowLoader(false);
    clearAllTimers();
    startIdleTimer();
  }, [startIdleTimer]);

  const handleCloseDialog = useCallback(async () => {
    await handleLogout();
  }, [handleLogout]);

  return (
    <ActionDialog
      open={isInactive}
      type="warning"
      icon={WarningIcon}
      heading={t("idleTimer.heading")}
      onClose={handleCloseDialog}
      onConfirm={handleConfirmDialog}
      SecondaryButtonText={t("idleTimer.logout")}
      PrimarButtonText={t("idleTimer.resume")}
    >
      <p>{t("idleTimer.body")}</p>

      <div className="font-semibold mt-3 flex items-center justify-start gap-2">
        {showLoader ? (
          <BeatLoader
            color="#005159"
            aria-label="Loading Spinner"
            size={10}
            data-testid="loader"
          />
        ) : (
          <span>
            {translations.idleTimer.timeLeft(
              String(Math.floor(countdown / 60)).padStart(2, "0"),
              String(countdown % 60).padStart(2, "0"),
            )}
          </span>
        )}
      </div>
    </ActionDialog>
  );
};

export default IdleTimerComponent;
